import { type ReactNode, Ref, useRef, useState } from 'react';

import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { ActionIconButton } from '@backrs_ui/common-ui/src/atoms/ActionIconButton';
import { BadgeCircle } from '@backrs_ui/common-ui/src/atoms/BadgeCircle';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { SENDER_TYPE, SenderType } from '@backrs_ui/common-ui/src/models/lubav';
import { copyTable, downloadTableCsv } from '@backrs_ui/common-ui/src/molecules/Lubav/tableExport';

import {
  Box,
  Link,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const MESSAGE_STRINGS = appStrings.modules.lubav.message;

const NUGGET_TYPES = [
  'major',
  'career_path',
  'scholarship',
  'college',
  'skill',
  'story',
  'competency',
  'job_opportunity',
  'industry',
  'tool',
  'employer',
  'suggested_playbook',
  'coach',
  'deep_dive',
] as const;

const NUGGET_HASH_PATTERN = /^#nugget:([^:]+):(.+)$/;
const DOCUMENT_HASH_PATTERN = /^#document:(.+)$/;
const SIGNUP_HASH = '#signup';

// Transform [text](career_path:123) -> [text](#nugget:career_path:123)
const preprocessNuggetReferences = (text: string): string => {
  const nuggetTypesPattern = NUGGET_TYPES.join('|');
  // matches [text](nuggetType:id) patterns
  const linkPattern = new RegExp(`\\[([^\\]]+)\\]\\((${nuggetTypesPattern}):([^)]+)\\)`, 'g');

  const result = text.replace(linkPattern, (_, linkText, nuggetType, nuggetId) => {
    return `[${linkText}](#nugget:${nuggetType}:${nuggetId})`;
  });

  return result;
};

export type MessageProps = Readonly<{
  id?: string;
  text: string;
  additionalText?: string;
  // Rendered inside the bubble, above the text.
  leading?: React.ReactNode;
  component: React.ReactNode;
  componentInsideMessage?: boolean;
  actionButtons?: React.ReactNode;
  sender: SenderType;

  // props for editing
  onEdit?: () => void;
  isEditing?: boolean;
  editingText?: string;
  onEditingTextChange?: (text: string) => void;
  onEditSubmit?: () => void;
  onEditCancel?: () => void;
  isNumeric?: boolean;

  // props for nugget reference click highlighting
  onNuggetReferenceClick?: (nuggetType: string, nuggetId: string) => void;
  isNuggetAvailable?: (nuggetType: string, nuggetId: string) => boolean;

  // public url for a nugget reference, so a copied message carries a real link
  getNuggetHref?: (nuggetType: string, nuggetId: string) => string | undefined;

  // props for document link clicks
  onDocumentClick?: (documentType: string) => void;

  // handler for #signup links (guest visitors); when absent the link renders as plain text
  onSignupClick?: () => void;

  // exposes the rendered markdown element so callers can copy it with its formatting
  contentRef?: Ref<HTMLDivElement>;
}>;

export const Message = ({
  id,
  text,
  additionalText,
  leading,
  component,
  componentInsideMessage,
  actionButtons,
  sender,
  onEdit,
  isEditing = false,
  editingText = '',
  onEditingTextChange,
  onEditSubmit,
  onEditCancel,
  isNumeric = false,
  onNuggetReferenceClick,
  isNuggetAvailable,
  getNuggetHref,
  onDocumentClick,
  onSignupClick,
  contentRef,
}: MessageProps) => {
  // pre-process nugget references to use nugget:// URL format & handle newlines
  const processedText = preprocessNuggetReferences(text).replace(/\n(?!\n)/g, '  \n');
  const isMobile = useMediaQuery('(max-width: 900px)');
  const { backrs } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  const canUserEdit = !isEditing && sender === SENDER_TYPE.USER;

  // A message with nothing for the bubble (e.g. a document card below it) skips the empty bubble.
  const showMessageBubble =
    isEditing || !!text.trim() || !!leading || (componentInsideMessage && !!component);

  const handleSubmit = () => {
    if (onEditSubmit) {
      onEditSubmit();
    }
  };

  const handleCancel = () => {
    if (onEditCancel) {
      onEditCancel();
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    } else if (event.key === 'Escape') {
      handleCancel();
    }
  };

  const renderEditComponent = () => (
    <Box
      contentEditable
      suppressContentEditableWarning
      onInput={(e) => {
        const value = e.currentTarget.textContent || '';

        if (isNumeric) {
          onEditingTextChange?.(value.replace(/[^0-9]/g, ''));
        } else {
          onEditingTextChange?.(value);
        }
      }}
      onKeyDown={handleKeyPress}
      sx={editBoxSx}
      ref={(el: HTMLDivElement | null) => {
        if (el && editingText !== el.textContent) {
          el.textContent = editingText;

          setTimeout(() => {
            const selection = window.getSelection();
            const range = document.createRange();

            range.selectNodeContents(el);
            range.collapse(false);
            selection?.removeAllRanges();
            selection?.addRange(range);
          }, 0);
        }
        if (el) {
          el.focus();
        }
      }}
    />
  );

  const renderMessageContent = () => (
    <Box ref={contentRef} sx={messageContentSx}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => {
            // check if this is a nugget reference (e.g., #nugget:career_path:123)
            const nuggetMatch = href ? NUGGET_HASH_PATTERN.exec(href) : null;

            if (nuggetMatch) {
              const [, nuggetType, nuggetId] = nuggetMatch;

              if (isNuggetAvailable && !isNuggetAvailable(nuggetType, nuggetId)) {
                return <>{children}</>;
              }

              const nuggetHref = getNuggetHref?.(nuggetType, nuggetId);

              if (nuggetHref) {
                return (
                  <Link
                    id={`nugget-ref-${nuggetType}-${nuggetId}`}
                    href={nuggetHref}
                    data-copy-style={JSON.stringify({
                      color: backrs.colors['Blue/500'],
                      textDecoration: 'underline',
                    })}
                    sx={nuggetReferenceStyle}
                    onClick={(event: React.MouseEvent) => {
                      event.preventDefault();
                      onNuggetReferenceClick?.(nuggetType, nuggetId);
                    }}
                  >
                    {children}
                  </Link>
                );
              }

              return (
                <Box
                  id={`nugget-ref-${nuggetType}-${nuggetId}`}
                  component="span"
                  sx={nuggetReferenceStyle}
                  onClick={() => onNuggetReferenceClick?.(nuggetType, nuggetId)}
                >
                  {children}
                </Box>
              );
            }

            // check if this is a signup link (e.g., #signup) emitted for guest visitors
            if (href === SIGNUP_HASH) {
              if (!onSignupClick) {
                return <>{children}</>;
              }

              return (
                <Link
                  id="message-signup-link"
                  component="span"
                  sx={documentLinkStyle}
                  onClick={onSignupClick}
                >
                  {children}
                </Link>
              );
            }

            // check if this is a document reference (e.g., #document:resume)
            const documentMatch = href ? DOCUMENT_HASH_PATTERN.exec(href) : null;

            if (documentMatch) {
              const [, documentType] = documentMatch;

              return (
                <Link
                  id="message-document-link"
                  component="span"
                  sx={documentLinkStyle}
                  onClick={() => onDocumentClick?.(documentType)}
                >
                  {children}
                </Link>
              );
            }

            // regular links are rendered normally
            return (
              <Link href={href} target="_blank" rel="noopener noreferrer" sx={linkStyle}>
                {children}
              </Link>
            );
          },
          h1: ({ children }) => (
            <Typography variant="text-xl-semibold" sx={headingStyle}>
              {children}
            </Typography>
          ),
          h2: ({ children }) => (
            <Typography variant="text-lg-semibold" sx={headingStyle}>
              {children}
            </Typography>
          ),
          h3: ({ children }) => (
            <Typography variant="text-md-semibold" sx={headingStyle}>
              {children}
            </Typography>
          ),
          h4: ({ children }) => (
            <Typography variant="text-sm-semibold" sx={headingStyle}>
              {children}
            </Typography>
          ),
          h5: ({ children }) => (
            <Typography variant="text-sm-semibold" sx={headingStyle}>
              {children}
            </Typography>
          ),
          h6: ({ children }) => (
            <Typography variant="text-sm-semibold" sx={headingStyle}>
              {children}
            </Typography>
          ),
          blockquote: ({ children }) => <Box sx={blockquoteStyle}>{children}</Box>,
          table: ({ children, node }) => (
            <CopyableTable id={id && `${id}-table-${node?.position?.start.offset}`}>
              {children}
            </CopyableTable>
          ),
          thead: ({ children }) => (
            <Box component="thead" sx={theadStyle}>
              {children}
            </Box>
          ),
          th: ({ children }) => (
            <Box component="th" sx={thStyle}>
              {children}
            </Box>
          ),
          td: ({ children }) => (
            <Box component="td" sx={tdStyle}>
              {children}
            </Box>
          ),
          code: ({ children, className }) => {
            const isBlock = className?.startsWith('language-');

            return isBlock ? (
              <Box sx={codeBlockStyle}>
                <Box component="code" sx={codeTextStyle}>
                  {children}
                </Box>
              </Box>
            ) : (
              <Box component="code" sx={inlineCodeStyle}>
                {children}
              </Box>
            );
          },
          pre: ({ children }) => <>{children}</>,
          ul: ({ children }) => <ul style={listStyle}>{children}</ul>,
          ol: ({ children, start }) => (
            <ol style={listStyle} start={start}>
              {children}
            </ol>
          ),
          li: ({ children }) => <li style={listItemStyle(isMobile)}>{children}</li>,
          p: ({ children }) => <p style={paragraphStyle}>{children}</p>,
        }}
      >
        {additionalText && isExpanded
          ? `${processedText}\n\n${preprocessNuggetReferences(additionalText)}`
          : processedText}
      </ReactMarkdown>
      {additionalText && (
        <Typography
          onClick={() => setIsExpanded((prev) => !prev)}
          sx={readMoreStyle}
          variant="text-md-semibold"
        >
          {isExpanded ? MESSAGE_STRINGS.lessContext : MESSAGE_STRINGS.moreContext}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box id={id} sx={containerSx(sender)} className="message-container">
      {sender === SENDER_TYPE.USER && (
        <Typography variant="text-sm-medium">{MESSAGE_STRINGS.you}</Typography>
      )}
      <Box className="message-content" sx={messageContentWrapperSx}>
        <Box sx={messageRowSx(sender)}>
          {sender === SENDER_TYPE.AI && (
            <Box sx={badgeCircleOffsetSx}>
              <BadgeCircle size="lg" />
            </Box>
          )}
          {canUserEdit && isMobile && (
            <Box onClick={onEdit} sx={editIconSx(isMobile)}>
              <AppIcon iconName="Edit01" diameter="16px" color="Gray/500" strokeWidth="bold" />
            </Box>
          )}
          {showMessageBubble && (
            <Box sx={messageContainerSx(sender)} key={isEditing ? 'editing' : 'viewing'}>
              {leading}
              {isEditing ? renderEditComponent() : renderMessageContent()}
              {componentInsideMessage && component}
            </Box>
          )}
        </Box>
        {actionButtons && <Box sx={actionButtonsContainerSx}>{actionButtons}</Box>}
      </Box>
      {!componentInsideMessage && component && (
        <Box sx={componentContainerSx(sender)}>{component}</Box>
      )}
      {canUserEdit && !isMobile && (
        <Box onClick={onEdit} sx={editIconSx(isMobile)}>
          <AppIcon iconName="Edit01" diameter="16px" color="Gray/500" strokeWidth="bold" />
        </Box>
      )}
      {isEditing && (
        <Box sx={editButtonsSx}>
          <Button
            id="message-edit-submit-button"
            size="sm"
            onClick={handleSubmit}
            hierarchy="Primary"
          >
            {MESSAGE_STRINGS.submit}
          </Button>
          <Button
            id="message-edit-cancel-button"
            size="sm"
            onClick={handleCancel}
            hierarchy="Secondary gray"
          >
            {MESSAGE_STRINGS.cancel}
          </Button>
        </Box>
      )}
    </Box>
  );
};

const containerSx =
  (sender: SenderType): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: sender === SENDER_TYPE.USER ? 'flex-end' : 'flex-start',
    justifyContent: sender === SENDER_TYPE.USER ? 'flex-end' : 'flex-start',
    gap: backrs.spacing['spacing-xs'],
    width: '100%',
  });

const messageRowSx =
  (sender: SenderType): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: sender === SENDER_TYPE.USER ? 'flex-end' : 'flex-start',
    justifyContent: sender === SENDER_TYPE.USER ? 'flex-end' : 'flex-start',
    gap: backrs.spacing['spacing-md'],
    width: '100%',
    minWidth: 0,
  });

const badgeCircleOffsetSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  top: backrs.spacing['spacing-xxs'],
});

const headingStyle: SxProps<Theme> = {
  margin: 0,
};

const TABLE_CSV_NAME = 'table.csv';

const CopyableTable = ({ id, children }: Readonly<{ id?: string; children: ReactNode }>) => {
  const tableRef = useRef<HTMLTableElement>(null);

  return (
    <Box id={id} sx={copyableTableSx}>
      <Box sx={tableActionsSx}>
        <ActionIconButton
          id={id && `${id}-copy-btn`}
          iconName="Copy01"
          label={MESSAGE_STRINGS.copyTable}
          doneLabel={MESSAGE_STRINGS.copied}
          failedLabel={MESSAGE_STRINGS.copyFailed}
          onAction={() => (tableRef.current ? copyTable(tableRef.current) : Promise.resolve(false))}
        />
        <ActionIconButton
          id={id && `${id}-download-btn`}
          iconName="Download01"
          label={MESSAGE_STRINGS.downloadCsv}
          doneLabel={MESSAGE_STRINGS.downloaded}
          failedLabel={MESSAGE_STRINGS.downloadFailed}
          onAction={() =>
            tableRef.current
              ? downloadTableCsv(tableRef.current, TABLE_CSV_NAME)
              : Promise.resolve(false)
          }
        />
      </Box>
      <Box sx={tableWrapperStyle}>
        <Box component="table" ref={tableRef} sx={tableStyle}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

const copyableTableSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
};

const tableActionsSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
};

const tableWrapperStyle: SxProps<Theme> = {
  overflowX: 'auto',
  width: '100%',
};

const tableStyle: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  borderCollapse: 'collapse',
  borderRadius: backrs.radius['radius-sm'],
  overflow: 'hidden',
});

const theadStyle: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Gray/100'],
});

const thStyle: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-sm-semibold'],
  padding: `${backrs.spacing['spacing-sm']} ${backrs.spacing['spacing-md']}`,
  textAlign: 'left',
  borderBottom: `1px solid ${backrs.colors['Gray/200']}`,
});

const tdStyle: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-sm-regular'],
  padding: `${backrs.spacing['spacing-sm']} ${backrs.spacing['spacing-md']}`,
  borderBottom: `1px solid ${backrs.colors['Gray/100']}`,
});

const codeBlockStyle: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Gray/100'],
  borderRadius: backrs.radius['radius-sm'],
  padding: backrs.spacing['spacing-md'],
  overflowX: 'auto',
});

const codeTextStyle: SxProps<Theme> = ({ typography }) => ({
  ...typography['text-sm-regular'],
  whiteSpace: 'pre',
});

const inlineCodeStyle: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-sm-regular'],
  backgroundColor: backrs.colors['Gray/100'],
  borderRadius: backrs.radius['radius-xs'],
  padding: `${backrs.spacing['spacing-xxs']} ${backrs.spacing['spacing-xs']}`,
});

const blockquoteStyle: SxProps<Theme> = ({ backrs }) => ({
  borderLeft: `3px solid ${backrs.colors['Brand/300']}`,
  paddingLeft: backrs.spacing['spacing-md'],
  margin: 0,
  fontStyle: 'italic',
  opacity: 0.85,
});

const listStyle: React.CSSProperties = {
  paddingLeft: '24px',
  margin: 0,
};

const paragraphStyle: React.CSSProperties = {
  margin: 0,
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
};

const listItemStyle = (isMobile: boolean): React.CSSProperties => ({
  marginBottom: isMobile ? '2px' : '4px',
  marginTop: isMobile ? '2px' : '4px',
});

const linkStyle: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Blue/500'],
  fontWeight: 'bold',
});

const documentLinkStyle: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Blue/500'],
  fontWeight: 'bold',
  cursor: 'pointer',
  textDecoration: 'underline',
  '&:hover': {
    opacity: 0.7,
  },
});

// a nugget reference opens a panel rather than navigating, so it doesn't look like a web link
const nuggetReferenceStyle: SxProps<Theme> = ({ backrs }) => ({
  fontWeight: 'bold',
  cursor: 'pointer',
  color: 'inherit',
  textDecoration: 'none',
  borderBottom: `1px dashed ${backrs.colors['Brand/500']}`,
  transition: 'background-color 0.15s ease-in-out',
  '&:hover': {
    backgroundColor: backrs.colors['Brand/100'],
  },
});

const readMoreStyle: SxProps<Theme> = ({ backrs }) => ({
  cursor: 'pointer',
  width: 'fit-content',
  color: backrs.colors['Blue/500'],
  '&:hover': {
    opacity: 0.7,
  },
});

const messageContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
  minWidth: 0,
});

const messageContainerSx =
  (sender: SenderType): SxProps<Theme> =>
  ({ backrs, typography }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: 'fit-content',
    maxWidth: '100%',
    minWidth: 0,
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    ...typography['text-md-regular'],
    ...(sender === SENDER_TYPE.USER
      ? {
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
          color: backrs.colors['Colors/Text/text-white'],
          backgroundColor: backrs.colors['Brand/500'],
          border: `1px solid ${backrs.colors['Beige/950']}`,
          padding: `${backrs.spacing['spacing-md']} ${backrs.spacing['spacing-lg']}`,
          borderRadius: `${backrs.radius['radius-md']} 0 ${backrs.radius['radius-md']} ${backrs.radius['radius-md']}`,
        }
      : {
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          color: backrs.colors['Colors/Text/text-primary'],
        }),
  });

const messageContentWrapperSx: SxProps<Theme> = {
  width: '100%',
  minWidth: 0,
};

const actionButtonsContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
  width: '100%',
  maxHeight: { xs: '40px', md: 0 },
  overflow: 'hidden',
  transition: 'max-height 0.2s ease-in-out',
  '.message-content:hover &': {
    maxHeight: '40px',
  },
};

const componentContainerSx =
  (sender: SenderType): SxProps<Theme> =>
  ({ backrs }) => {
    const isUser = sender === SENDER_TYPE.USER;

    // The inset lines AI components up past the badge circle. User messages have no
    // badge and hug the right edge, so the same inset just holds them off it.
    const inset = `calc(${backrs.spacing['spacing-3xl']} + ${backrs.spacing['spacing-xs']})`;

    return {
      display: 'flex',
      flexDirection: 'column',
      alignItems: isUser ? 'flex-end' : 'flex-start',
      ml: isUser ? 0 : inset,
      pr: isUser ? 0 : inset,
      width: '100%',
    };
  };

const editButtonsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-sm'],
  justifyContent: 'flex-end',
  marginTop: backrs.spacing['spacing-xs'],
});

const editBoxSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-md-regular'],
  color: backrs.colors['Colors/Text/text-white'],
  backgroundColor: backrs.colors['Colors/Base/Transparent'],
  border: 'none',
  outline: 'none',
  width: 'fit-content',
  minWidth: '20px',
  maxWidth: '100%',
  whiteSpace: 'pre-wrap',
  wordWrap: 'break-word',
  cursor: 'text',
  '&:focus': {
    outline: 'none',
  },
});

const editIconSx =
  (isMobile: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.5,
      backgroundColor: backrs.colors['Colors/Base/Transparent'],
    },
    ...(!isMobile && {
      opacity: 0,
      visibility: 'hidden',
      transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
      '.message-container:hover &': {
        opacity: 1,
        visibility: 'visible',
      },
    }),
  });
